import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Lightning, Globe, Gauge, ChartLineUp, ShieldCheck, Waves,
  List, X, Sun, Moon, Rocket, Users, Timer, UploadSimple,
  GearSix, TrendUp, Target, Storefront, Link as LinkIcon,
  Newspaper, MapPin, ArrowRight, CheckCircle, CaretRight,
  Star, UserCircle, FileText, Plugs, MagnifyingGlass,
  Broadcast, ShoppingBag, Browsers
} from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Logo, LogoText } from '../components/Logo';

const fadeUp = { initial: { y: 24, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } };
const stagger = { animate: { transition: { staggerChildren: 0.08 } } };

export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const features = [
    { icon: Lightning, title: 'Instant Submission', desc: 'Instant URL submission system', color: 'var(--accent)' },
    { icon: Timer, title: '2-Minute Indexing', desc: 'Average indexing time: 2 minutes', color: 'var(--accent)' },
    { icon: Globe, title: 'Bulk Support', desc: 'Bulk URL support for agencies & professionals', color: 'var(--accent)' },
    { icon: Gauge, title: 'Live Dashboard', desc: 'Clean dashboard with real-time status tracking', color: 'var(--accent)' },
    { icon: ShieldCheck, title: 'Safe Process', desc: 'Safe and optimized indexing process', color: 'var(--accent)' },
    { icon: TrendUp, title: 'High Success', desc: 'High success ratio with stable performance', color: 'var(--accent)' },
    { icon: Users, title: 'Built for Pros', desc: 'Designed for SEO experts, marketers, and website owners', color: 'var(--accent)' },
  ];

  const audiences = [
    { icon: Target, label: 'SEO agencies' },
    { icon: ChartLineUp, label: 'Affiliate marketers' },
    { icon: Newspaper, label: 'News & blog websites' },
    { icon: MapPin, label: 'Local SEO campaigns' },
    { icon: Storefront, label: 'Ecommerce stores' },
    { icon: LinkIcon, label: 'PBN & backlink indexing' },
  ];

  const steps = [
    { num: '01', title: 'Submit URLs', desc: 'Upload single or bulk URLs directly from your dashboard.', icon: UploadSimple },
    { num: '02', title: 'Processing Starts Instantly', desc: 'Our system begins processing your submissions immediately.', icon: GearSix },
    { num: '03', title: 'Get Indexed Faster', desc: 'Most URLs start showing indexing activity within 2 minutes.', icon: Rocket },
  ];

  const useCases = [
    { icon: Browsers, title: 'Website Content Indexing', desc: 'New pages, blog posts, and updated content that Google hasn\'t picked up yet. Stop waiting weeks — push it through in minutes.' },
    { icon: LinkIcon, title: 'Backlink & PBN Indexing', desc: 'You built the links. Now make sure Google actually sees them. Submit your backlinks and watch them appear in search results.' },
    { icon: Newspaper, title: 'News & Time-Sensitive Pages', desc: 'Breaking stories, press releases, event pages — content that loses value when it sits unindexed. Get it live while it matters.' },
    { icon: ShoppingBag, title: 'Ecommerce Product Pages', desc: 'New product drops, seasonal collections, restocked items. Your customers can\'t buy what they can\'t find on Google.' },
    { icon: MapPin, title: 'Local Business & GMB Posts', desc: 'Location pages, service area content, Google Business posts. Local SEO moves fast — your indexing should too.' },
    { icon: Plugs, title: 'API-Driven Bulk Workflows', desc: 'Hook into our REST API and automate indexing from your CMS, deployment pipeline, or content management system.' },
  ];

  const faqs = [
    { q: 'How does this compare to submitting through Google Search Console?', a: 'Search Console caps you at a handful of daily submissions and there\'s no bulk option. We handle up to 150 URLs per batch with no daily cap — and our success rate sits well above manual GSC submissions.' },
    { q: 'What kind of success rate should I expect?', a: 'Most URLs we process get indexed within 2 minutes. Overall, we see a 97% success rate across all submissions. If a URL is publicly accessible and crawlable, it\'ll get picked up.' },
    { q: 'Which cryptocurrencies can I pay with?', a: 'We take BTC, ETH, USDT (BEP20 & TRC20), SOL, BNB, LTC, DOGE, TRX, XRP, and MATIC. Credits hit your account the moment the blockchain confirms.' },
    { q: 'Can I integrate this into my existing tools?', a: 'Every plan includes full API access. You get documented endpoints for submitting URLs, checking status, and managing credits. Most teams have it running within an hour.' },
    { q: 'Do credits expire? Is there a subscription?', a: 'No subscriptions, no expiry. You buy credits once and use them whenever you need. No recurring charges, no surprise renewals.' },
  ];

  const reviews = [
    { name: 'James K.', role: 'Runs an SEO agency', text: 'We were spending 2 hours a day on manual GSC submissions across 30+ client sites. Now it\'s a 5-minute batch job. Honestly wish I\'d found this sooner.', rating: 5 },
    { name: 'Sarah M.', role: 'Affiliate marketer', text: 'My review pages used to sit in limbo for weeks. Now they rank within days because Google actually finds them right away. The ROI on this is insane.', rating: 5 },
    { name: 'David R.', role: 'Runs a news site', text: 'We push 40-50 articles a day. Before this, half of them wouldn\'t show up in search for 3-4 days. Now everything\'s indexed same-day. Traffic is up 35%.', rating: 5 },
    { name: 'Priya S.', role: 'Local SEO consultant', text: 'For local businesses, getting new service pages indexed fast is everything. This tool just works. Set it up once, forget about it. Crypto payments are a nice touch too.', rating: 5 },
    { name: 'Marcus T.', role: 'Ecommerce store owner', text: 'We drop new products every week and they\'d take forever to appear on Google. Hooked the API into our Shopify flow and now products are searchable within hours of going live.', rating: 5 },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-main)', color: 'var(--text-primary)' }}>
      {/* ===== NAVBAR ===== */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl border-b" style={{ background: 'var(--backdrop-blur)', borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <Logo size="md" />
            <LogoText size="md" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/about" className="nav-link" data-testid="nav-about">About</Link>
            <Link to="/faq" className="nav-link" data-testid="nav-faq">FAQ</Link>
            <Link to="/pricing" className="nav-link" data-testid="nav-pricing">Pricing</Link>
            <Link to="/docs" className="nav-link" data-testid="nav-docs">API Docs</Link>
            <Link to="/support" className="nav-link" data-testid="nav-support">Support</Link>
            <Link to="/login" className="nav-link" data-testid="nav-login">Login</Link>
            <button onClick={toggleTheme} className="p-2 rounded-lg transition-colors hover:bg-[var(--bg-hover)]" style={{ color: 'var(--text-muted)' }} data-testid="landing-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link to="/register" className="btn-primary text-sm px-5 py-2.5" data-testid="nav-get-started">
              Get Started <ArrowRight weight="bold" className="w-4 h-4 inline ml-1" />
            </Link>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg" style={{ color: 'var(--text-muted)' }} data-testid="mobile-theme-toggle">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 rounded-lg" data-testid="landing-mobile-menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <List className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden border-t px-6 py-4 space-y-3" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)' }}>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>About</Link>
            <Link to="/faq" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>FAQ</Link>
            <Link to="/pricing" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Pricing</Link>
            <Link to="/docs" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>API Docs</Link>
            <Link to="/support" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Support</Link>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block py-2 text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>Login</Link>
            <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="btn-primary text-sm text-center block">Get Started</Link>
          </div>
        )}
      </nav>

      {/* ===== HERO ===== */}
      <section className="pt-28 sm:pt-36 pb-20 sm:pb-32 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-3xl animate-float" style={{ background: 'rgba(225, 29, 72, 0.04)' }} />
          <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] rounded-full blur-3xl animate-float" style={{ animationDelay: '3s', background: 'rgba(225, 29, 72, 0.03)' }} />
        </div>

        <motion.div className="max-w-7xl mx-auto relative" initial="initial" animate="animate" variants={stagger}>
          <div className="max-w-3xl">
            <motion.h1
              variants={fadeUp}
              className="text-4xl sm:text-5xl lg:text-7xl font-extrabold tracking-tighter leading-none mb-6"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
              data-testid="hero-heading"
            >
              Instant Google Indexing{' '}
              <span style={{ color: 'var(--accent)' }}>in Just 2 Minutes</span>
            </motion.h1>

            <motion.p variants={fadeUp} className="text-base sm:text-lg leading-relaxed mb-3 max-w-2xl" style={{ color: 'var(--text-secondary)' }} data-testid="hero-description">
              Welcome to <span className="font-extrabold" style={{ color: 'var(--accent)' }}>index24hrs.com</span> — the smarter way to get your URLs indexed faster and improve visibility without waiting days for search engines to crawl your pages.
            </motion.p>

            <motion.p variants={fadeUp} className="text-sm leading-relaxed mb-8 max-w-2xl" style={{ color: 'var(--text-muted)' }}>
              Whether you manage client websites, local SEO campaigns, affiliate projects, or large-scale publishing networks, our platform is built to deliver fast and reliable indexing with minimal effort.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 mb-12">
              <Link to="/register" className="btn-primary flex items-center gap-2 text-base" data-testid="hero-get-started">
                <Lightning weight="bold" className="w-5 h-5" />
                Get Started Today
                <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
              <Link to="/login" className="btn-secondary text-base" data-testid="hero-login">
                Sign In
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-center gap-8 sm:gap-12">
              {[
                { val: '2 min', label: 'Avg Index Time' },
                { val: '150+', label: 'URLs Per Batch' },
                { val: '97%', label: 'Success Rate' },
              ].map((s, i) => (
                <div key={i}>
                  <p className="text-2xl sm:text-3xl font-extrabold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.val}</p>
                  <p className="text-xs sm:text-sm" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ===== WHY CHOOSE ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="why-choose-heading">
                Why Choose index24hrs<span style={{ color: 'var(--accent)' }}>.com</span>?
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="group border rounded-xl p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 cursor-default"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}
                  data-testid={`why-choose-${i}`}
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110" style={{ background: 'var(--accent-bg)' }}>
                    <f.icon weight="duotone" className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                  </div>
                  <h3 className="font-bold text-base mb-1">{f.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== SPEED & SCALE ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true, margin: "-100px" }} variants={stagger} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div variants={fadeUp} className="space-y-6">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="speed-scale-heading">
                Built for Speed<br />& Scale
              </h2>
              <p className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                Stop waiting for search engines to discover your content naturally. With index24hrs.com, your pages are pushed quickly so new posts, backlinks, and landing pages can appear faster in search results.
              </p>
            </motion.div>

            <motion.div variants={fadeUp}>
              <p className="text-xl sm:text-2xl font-black mb-5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Perfect for:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {audiences.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 border rounded-lg px-4 py-3 transition-all duration-200 hover:border-green-600/30" style={{ borderColor: 'var(--border-main)' }} data-testid={`perfect-for-${i}`}>
                    <a.icon weight="duotone" className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--accent)' }} />
                    <span className="text-sm font-medium">{a.label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="process-heading">
                How It Works
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((s, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="relative text-center border rounded-2xl p-8 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}
                  data-testid={`process-step-${i}`}
                >
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: 'var(--accent)' }}>
                    {s.num}
                  </div>
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mt-2 mb-5 transition-transform duration-300 group-hover:scale-110" style={{ background: 'var(--accent-bg)' }}>
                    <s.icon weight="duotone" className="w-8 h-8" style={{ color: 'var(--accent)' }} />
                  </div>
                  <h3 className="text-lg font-bold mb-2">{s.title}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== PROFESSIONAL ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="border rounded-2xl p-10 sm:p-16 relative overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}>
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--accent)' }} />
              <div className="text-center space-y-5">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="professional-heading">
                  Professional Indexing Solution
                </h2>
                <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  At index24hrs.com, performance and reliability come first. Our infrastructure is optimized to handle both small and high-volume indexing requirements efficiently.
                </p>
                <p className="text-base leading-relaxed max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  If you're serious about faster indexing and better workflow management, this is the platform built for you.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== USE CASES ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                What You Can <span style={{ color: 'var(--accent)' }}>Index</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {useCases.map((uc, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="border rounded-2xl p-7 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}
                  data-testid={`use-case-${i}`}
                >
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 transition-transform duration-300 group-hover:scale-110" style={{ background: 'var(--accent-bg)' }}>
                    <uc.icon weight="duotone" className="w-8 h-8" style={{ color: 'var(--accent)' }} />
                  </div>
                  <h3 className="text-lg font-black mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{uc.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{uc.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Simple <span style={{ color: 'var(--accent)' }}>Pricing</span>
              </h2>
              <p className="text-base mt-3" style={{ color: 'var(--text-secondary)' }}>No subscriptions. No hidden fees. Pay with cryptocurrency.</p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {[
                { price: '$20', urls: '1,000', perUrl: '$0.020', id: 'starter' },
                { price: '$90', urls: '5,000', perUrl: '$0.018', popular: true, id: 'growth' },
                { price: '$225', urls: '15,000', perUrl: '$0.015', id: 'pro' },
                { price: '$650', urls: '50,000', perUrl: '$0.013', id: 'business' },
                { price: '$1,050', urls: '100,000', perUrl: '$0.0105', id: 'enterprise' },
              ].map((p, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="border rounded-2xl p-6 sm:p-8 text-center transition-all duration-300 hover:-translate-y-1 relative"
                  style={{ background: 'var(--bg-card)', borderColor: p.popular ? 'var(--accent)' : 'var(--border-main)', boxShadow: p.popular ? '0 0 30px rgba(225, 29, 72, 0.08)' : 'var(--shadow-card)' }}
                  data-testid={`pricing-card-${i}`}
                >
                  {p.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white" style={{ background: 'var(--accent)' }}>
                      POPULAR
                    </div>
                  )}
                  <p className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{p.price}</p>
                  <p className="text-base font-bold mt-1" style={{ color: 'var(--accent)' }}>{p.urls} URLs</p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{p.perUrl} per URL</p>
                  <Link to="/pricing" className="btn-primary w-full mt-6 text-sm py-2.5 flex items-center justify-center gap-1" data-testid={`pricing-select-${i}`}>
                    Select <CaretRight weight="bold" className="w-4 h-4" />
                  </Link>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Frequently <span style={{ color: 'var(--accent)' }}>Asked</span>
              </h2>
            </motion.div>

            <div className="space-y-5">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="border rounded-xl p-6 sm:p-7 transition-all duration-200 hover:border-[var(--border-accent)]"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}
                  data-testid={`landing-faq-${i}`}
                >
                  <h3 className="text-base sm:text-lg font-black mb-3 leading-snug" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    {faq.q}
                  </h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {faq.a}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div variants={fadeUp} className="text-center mt-10">
              <Link to="/faq" className="text-sm font-bold inline-flex items-center gap-1.5 transition-colors hover:underline" style={{ color: 'var(--accent)' }} data-testid="landing-faq-more">
                View all FAQs <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== SEO REVIEWS ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="text-center mb-12">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                What Our Users <span style={{ color: 'var(--accent)' }}>Say</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((r, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  className="border rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}
                  data-testid={`review-${i}`}
                >
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} weight="fill" className="w-4 h-4" style={{ color: s < r.rating ? '#FBBF24' : 'var(--border-main)' }} />
                    ))}
                  </div>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>"{r.text}"</p>
                  <div className="flex items-center gap-3 border-t pt-4" style={{ borderColor: 'var(--border-main)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-bg)' }}>
                      <UserCircle weight="duotone" className="w-6 h-6" style={{ color: 'var(--accent)' }} />
                    </div>
                    <div>
                      <p className="font-bold text-sm">{r.name}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 max-w-4xl mx-auto">
              {reviews.slice(3).map((r, i) => (
                <motion.div
                  key={i + 3}
                  variants={fadeUp}
                  className="border rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  style={{ background: 'var(--bg-card)', borderColor: 'var(--border-main)', boxShadow: 'var(--shadow-card)' }}
                  data-testid={`review-${i + 3}`}
                >
                  <div className="flex gap-0.5 mb-4">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} weight="fill" className="w-4 h-4" style={{ color: s < r.rating ? '#FBBF24' : 'var(--border-main)' }} />
                    ))}
                  </div>
                  <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>"{r.text}"</p>
                  <div className="flex items-center gap-3 border-t pt-4" style={{ borderColor: 'var(--border-main)' }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-bg)' }}>
                      <UserCircle weight="duotone" className="w-6 h-6" style={{ color: 'var(--accent)' }} />
                    </div>
                    <div>
                      <p className="font-bold text-sm">{r.name}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.role}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp} className="border rounded-2xl p-10 sm:p-16 relative overflow-hidden" style={{ background: 'var(--bg-card)', borderColor: 'var(--border-accent)', boxShadow: 'var(--shadow-card)' }}>
              <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--accent)' }} />
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="cta-heading">
                Get Started Today
              </h2>
              <p className="text-base mb-8" style={{ color: 'var(--text-secondary)' }}>
                Start indexing smarter with index24hrs.com and experience fast, stable, and professional-grade indexing performance.
              </p>
              <Link to="/register" className="btn-primary inline-flex items-center gap-2 text-base" data-testid="cta-get-started">
                <Lightning weight="bold" className="w-5 h-5" />
                Get Started Today
                <ArrowRight weight="bold" className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-16 sm:py-24 px-6 md:px-12 border-t" style={{ borderColor: 'var(--border-main)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-8 mb-8">
            <div className="flex items-center gap-3">
              <Logo size="sm" />
              <span className="text-lg font-extrabold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                index24hrs<span style={{ color: 'var(--accent)' }}>.com</span>
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-6 sm:gap-8">
              <Link to="/about" className="text-sm transition-colors hover:text-green-700 dark:hover:text-rose-400" style={{ color: 'var(--text-muted)' }} data-testid="footer-about">About Us</Link>
              <Link to="/faq" className="text-sm transition-colors hover:text-green-700 dark:hover:text-rose-400" style={{ color: 'var(--text-muted)' }} data-testid="footer-faq">FAQ</Link>
              <Link to="/refund" className="text-sm transition-colors hover:text-green-700 dark:hover:text-rose-400" style={{ color: 'var(--text-muted)' }} data-testid="footer-refund">Refund Policy</Link>
              <Link to="/support" className="text-sm transition-colors hover:text-green-700 dark:hover:text-rose-400" style={{ color: 'var(--text-muted)' }} data-testid="footer-support">Support</Link>
              <Link to="/docs" className="text-sm transition-colors hover:text-green-700 dark:hover:text-rose-400" style={{ color: 'var(--text-muted)' }} data-testid="footer-docs">API Docs</Link>
            </div>
          </div>
          <div className="border-t pt-8" style={{ borderColor: 'var(--border-main)' }}>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>© 2026 index24hrs.com. Instant indexing for the modern web.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
